import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { usePlatformGuard } from '@/hooks/usePlatformGuard'
import { fetchPostByUri, fetchViewerState, mergeViewerState } from '@/services/postsService'
import { fetchNostrEventById, fetchNostrProfiles, DEFAULT_RELAYS } from '@/services/nostrService'
import { nostrEventToPost } from '@/adapters/nostrAdapter'

export function useParentPost(uri: string | undefined, relayHint?: string) {
    const { session, nostrSession } = useAuth()
    const isCompatible = usePlatformGuard('atproto', 'activitypub')

    const isNostr = !!uri?.startsWith('nostr:')

    const query = useQuery({
        queryKey: ['post', uri, !!session],
        queryFn: async () => {
            const post = await fetchPostByUri(uri!)
            if (!post) return null

            // Enrich with viewer engagement state
            if (session && !post.isActivityPub) {
                try {
                    const viewerState = await fetchViewerState(session, [post.uri])
                    return mergeViewerState([post], viewerState)[0]
                } catch (err) {
                    console.warn('[viewer state] enrichment failed:', err)
                }
            }
            return post
        },
        enabled: !!uri && isCompatible && !isNostr,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    })

    const nostrQuery = useQuery({
        queryKey: ['nostrParentPost', uri, relayHint],
        queryFn: async () => {
            const hexId = uri!.replace('nostr:', '')
            const relays = [
                ...DEFAULT_RELAYS,
                'wss://purplepag.es',
                ...(nostrSession?.relays ?? []),
                ...(relayHint ? [relayHint] : []),
            ]
            const event = await fetchNostrEventById(hexId, relays)
            if (!event) return null

            const profileMap = await fetchNostrProfiles([event.pubkey], relays)

            return nostrEventToPost(event, profileMap, undefined)
        },
        enabled: isNostr && !!uri,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    })

    if (isNostr) {
        return {
            parentPost: nostrQuery.data ?? null,
            isLoading: nostrQuery.isLoading,
            error: nostrQuery.error,
        }
    }

    return {
        parentPost: query.data ?? null,
        isLoading: query.isLoading,
        error: query.error,
    }
}
